import { Component, OnInit, OnDestroy } from '@angular/core';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { Router } from '@angular/router';
import { ProfilePopupComponent } from '../../profile-popup/profile-popup.component';
import { CommonModule } from '@angular/common';
import { NotificationPopupComponent } from '../../notification-popup/notification-popup.component';
import { ApiService } from '@app/services/api.service';
import { PricingModalService } from '@app/services/pricing-modal.service';
import { SubscriptionService } from '@app/services/subscription.service';
import { Subscription } from 'rxjs';
import { SnackbarService } from '@app/services/snackbar.service';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

interface TableColumn {
  key: string;
  label: string;
  width?: string;
}

interface TableData {
  [key: string]: any;
}

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [CommonModule, ProfilePopupComponent, NotificationPopupComponent],
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss']
})
export class HeaderComponent implements OnInit, OnDestroy {
  userInfo:any;
  isDropdownOpen = false;
  showNotificationPopup = false;
  showProfilePopup = false;
  showPricingModal = false;
  isAnnualBilling = false;
  plans: any[] = [];
  planAddOns:any;
  subscriptionInfo:any;
  showSubscriptionSuccess = false;
  showSubscriptionFailure = false;
  selectedPlan: any = null;
  private subscription = new Subscription();
  private destroy$ = new Subject<void>();
  boosterTypes = ['CONTACT', 'EMAIL', 'AGENT_CAMPAIGN', 'AGENT_RESEARCH'];
  selectedAddOnIds: number[] = [];
  boosterSuccessMessage = false;

  getPlanPrice(plan: any): number {
    if (this.isAnnualBilling) {
      return plan.annualPrice;
    }
    return plan.monthlyPrice;
  }

  toggleBilling() {
    this.isAnnualBilling = !this.isAnnualBilling;
  }

  isCurrentPlan(plan: any): boolean {
    const currentPlanId = this.subscriptionService.info?.planId;
    return currentPlanId && currentPlanId === plan.id;
  }

  getPlanButtonText(plan: any): string {
    if (this.isCurrentPlan(plan)) {
      return 'Current Plan';
    }
    
    const currentPlanId = this.subscriptionService.info?.planId;
    if (currentPlanId) {
      const currentPlan = this.plans.find(p => p.id === currentPlanId);
      if (currentPlan) {
        const currentPrice = this.getPlanPrice(currentPlan);
        const newPrice = this.getPlanPrice(plan);
        
        if (newPrice > currentPrice) {
          return 'Upgrade';
        } else if (newPrice < currentPrice) {
          return 'Downgrade';
        } else {
          return 'Switch';
        }
      }
      return 'Upgrade';
    }
    return plan.name === 'Free' ? 'Start for Free' : 'Choose ' + plan.name;
  }

  isButtonDisabled(plan: any): boolean {
    return this.isCurrentPlan(plan);
  }

  getCurrentPlanPrice(): number {
    const currentPlanId = this.subscriptionService.info?.planId;
    if (!currentPlanId) return 0;
    
    const currentPlan = this.plans.find(p => p.id === currentPlanId);
    if (!currentPlan) return 0;
    
    return this.getPlanPrice(currentPlan);
  }

  renewCurrentPlan() {
    const currentPlanId = this.subscriptionService.info?.planId;
    if (!currentPlanId) return;
    
    const currentPlan = this.plans.find(p => p.id === currentPlanId);
    if (currentPlan) {
      this.subscribe(currentPlan);
    }
  }


  tableColumns: TableColumn[] = [];

  tableData: TableData[] = [];

  constructor(
    private auth:AuthenticationService,
    private router:Router, 
    private apiService: ApiService,
    private snackbarService: SnackbarService,
    private pricingModalService: PricingModalService,
    public subscriptionService: SubscriptionService){}

  ngOnInit(): void {
    this.userInfo = this.auth.currentUserValue;
    this.subscription.add(
      this.pricingModalService.openPricingModal$
        .pipe(takeUntil(this.destroy$))
        .subscribe(show => {
          const warningModalOpen = (this.subscriptionService as any).showWarningModal;
          if (show && !warningModalOpen ) {
            this.openPricingModal();
          }
        })
    );
    this.subscription.add(
      this.subscriptionService.paymentSuccess$
        .pipe(takeUntil(this.destroy$))
        .subscribe(success => {
          if (success) {
            this.subscription.add(
              this.subscriptionService.load()
                .pipe(takeUntil(this.destroy$))
                .subscribe(() => {
                  this.showPricingModal = true;
                  this.subscriptionInfo = this.subscriptionService.info;
                  if(!this.subscriptionInfo.paid){
                    this.showSubscriptionFailure = true;
                  }else{
                    this.showSubscriptionSuccess = true;
                  }
                })
            );
          }
        })
    );
    this.subscription.add(
      this.subscriptionService.paymentFailure$
        .pipe(takeUntil(this.destroy$))
        .subscribe(failure => {
          if (failure) {
            this.showPricingModal = true;
            this.showSubscriptionFailure = true;
          }
        })
    );

    this.subscription.add(
      this.subscriptionService.boosterPackPaymentSuccess$
        .pipe(takeUntil(this.destroy$))
        .subscribe(success => {
          if (success) {
            this.subscription.add(
              this.subscriptionService.load()
                .pipe(takeUntil(this.destroy$))
                .subscribe(() => {
                  this.showPricingModal = true;
                  this.subscriptionInfo = this.subscriptionService.info;
                  if(!this.subscriptionInfo.paid){
                    this.showSubscriptionFailure = true;
                  }else{
                    this.showSubscriptionSuccess = true;
                    this.boosterSuccessMessage = true;
                  }
                })
            );
          }
        })
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggleDropdown() {
    this.isDropdownOpen = !this.isDropdownOpen;
  }

  toggleNotificationPopup() {
    this.showNotificationPopup = !this.showNotificationPopup;
  }

  closeNotificationPopup() {
    this.showNotificationPopup = false;
  }

  handleNotificationAction(event: any) {
    if (event === 'close') {
      this.closeNotificationPopup();
    }
  }

  toggleProfilePopup() {
    this.showProfilePopup = !this.showProfilePopup;
    this.isDropdownOpen = false;
  }

  subscribe(plan:any){
    if (this.isCurrentPlan(plan)) {
      return;
    }
    const billingCycle = this.isAnnualBilling ? 'ANNUAL' : 'MONTHLY';
    const detail = {planId : plan.id, billingCycle:billingCycle};
    if(plan.name === 'Free'){
      this.changePlan(plan,detail);
    }else{
      this.createCheckoutSession(plan,detail);
    }
  }

  createCheckoutSession(plan: string,detail) {
    this.subscription.add(
      this.apiService.checkout(detail)
        .pipe(takeUntil(this.destroy$))
        .subscribe(
          (res) => {
            if (res && res.checkoutUrl) {
              window.location.href = res.checkoutUrl;
            }else{
              this.snackbarService.show('Checkout Error', 'Something went Wrong, Please try later', 'error');
            }
          },
          (err) => {}
        )
    );
  }

  changePlan(plan:any,detail){
    this.subscription.add(
      this.apiService.changePlan(detail)
        .pipe(takeUntil(this.destroy$))
        .subscribe(
          (res) => {
            this.subscriptionInfo = res;
            this.selectedPlan = plan;
            this.showSubscriptionSuccess = true;
            this.subscriptionService.reload();
          },
          (err) => {}
        )
    );
  }
  subscribePlan(plan:any,detail){
    this.subscription.add(
      this.apiService.subscribe(detail)
        .pipe(takeUntil(this.destroy$))
        .subscribe(
          (res) => {
            this.subscriptionInfo = res;
            this.selectedPlan = plan;
            this.showSubscriptionSuccess = true;
            this.subscriptionService.reload();
          },
          (err) => {}
        )
    );
  }
  closeProfilePopup() {
    this.showProfilePopup = false;
  }

  handleProfileSubscribe() {
    this.closeProfilePopup();
    this.openPricingModal();
  }

  openPricingModal() {
    this.subscription.add(
      this.apiService.getSubscriptionPlans()
        .pipe(takeUntil(this.destroy$))
        .subscribe(
          (res) => {
            this.plans = res['plans'];
            this.showPricingModal = true;
            this.showSubscriptionSuccess = false;
            this.selectedPlan = null;
            this.isDropdownOpen = false;
          },
          (err) => {}
        )
    );
    this.getAddOnPlans();
  }

  getAddOnPlans(){
    this.subscription.add(
      this.apiService.getSubscriptionAddOns()
        .pipe(takeUntil(this.destroy$))
        .subscribe(
          (res) => {
            this.planAddOns = res['addOns'];
          },
          (err) => {}
        )
    );
  }
  goToHome() {
    this.showPricingModal = false;
    this.showSubscriptionSuccess = false;
    this.showSubscriptionFailure = false;
    this.selectedPlan = null;
    this.boosterSuccessMessage = false;
    this.subscriptionService.resetPaymentSuccess();
    this.subscriptionService.resetPaymentFailure();
    this.subscriptionService.resetBoosterPackPaymentSuccess();
    this.router.navigate(['/home']);
  }

  closePricingModal() {
    if(this.showSubscriptionFailure || this.showSubscriptionSuccess){
      this.showSubscriptionFailure = false;
      this.showSubscriptionSuccess = false;
      this.subscriptionService.resetPaymentSuccess();
      this.subscriptionService.resetPaymentFailure();
      this.subscriptionService.resetBoosterPackPaymentSuccess();
      this.router.navigate(['/home']);
    }
    this.selectedPlan = null;
    this.boosterSuccessMessage = false;
    this.showPricingModal = false;
    this.showSubscriptionSuccess = false;
  }

  logout(){
    localStorage.removeItem('subscriptionWarningShown');
    this.subscription.add(
      this.auth.logout()
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => this.router.navigate(['/sign-in']),
          error: (err) => {}
        })
    );
  }

  getBoosterTypeLabel(type: string): string {
    switch (type) {
      case 'CONTACT': return 'Verified Contacts Revealed';
      case 'EMAIL': return 'Emails Sent';
      case 'AGENT_CAMPAIGN': return 'AI Co-Pilot Campaign';
      case 'AGENT_RESEARCH': return 'AI Research Queries';
      default: return type;
    }
  }

  getBoosterTiers(): number[] {
    if (!this.planAddOns) return [];
    let max = 0;
    for (const type of this.boosterTypes) {
      if (this.planAddOns[type] && this.planAddOns[type].length > max) {
        max = this.planAddOns[type].length;
      }
    }
    return Array(max).fill(0).map((_, i) => i);
  }

  onAddOnCheckboxChange(addOnId: number, checked: boolean) {
    if (checked) {
      if (!this.selectedAddOnIds.includes(addOnId)) {
        this.selectedAddOnIds.push(addOnId);
      }
    } else {
      this.selectedAddOnIds = this.selectedAddOnIds.filter(id => id !== addOnId);
    }
  }

  onAddPlanBooster() {
    this.boosterSuccessMessage = false;
    const credits = { ids: this.selectedAddOnIds };
    this.subscription.add(
      this.apiService.creditsCheckout(credits)
        .pipe(takeUntil(this.destroy$))
        .subscribe(
          (res) => {
            if (res && res.checkoutUrl) {
              window.location.href = res.checkoutUrl;
            }else{
              this.snackbarService.show('Checkout Error', 'Something went Wrong, Please try later', 'error');
            }
          },
          (err) => {
            this.boosterSuccessMessage = false;
          }
        )
    );
  }

  getChecked(event: Event): boolean {
    return (event.target as HTMLInputElement).checked;
  }

  viewReceipt() {
    if (this.subscriptionInfo?.receiptUrl) {
      window.open(this.subscriptionInfo.receiptUrl, '_blank');
    }
  }
}
